import { AfterViewInit, Component, Inject, OnInit, Optional, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { forkJoin, Observable, of } from 'rxjs';
import { FileAttachment } from 'src/app/models/fileAttachment.model';
import { FileAttachmentService } from 'src/app/services/file-attachment.service';
import { CommonService } from 'src/app/services/common.service';
import { ToastService } from 'src/app/services/toast.service';
import { DateFormatService } from 'src/app/services/dateFormat.service';
import nameOf from 'src/app/shared/form/nameOf.helper';
import { CoreGridComponent } from 'src/app/shared/grid/core-grid/core-grid.component';
import { AuthService } from 'src/app/services/auth.service';
import { RolePermissionObjects } from 'src/app/shared/constants/rolePermissionObjects.constants';
import { GridViewService } from 'src/app/services/grid-view.service';
import { GridSettingsService } from 'src/app/shared/grid/core-grid/grid-settings-flyout/grid-settings.service';
import { ColumnConfig } from 'src/app/shared/grid/core-grid/columnConfig.model';
import { OrganizationLevelService } from 'src/app/services/organizationLevel.service';
import { UserService } from 'src/app/services/user.service';
import { Lookup } from 'src/app/models/lookup.model';
import { DocumentType } from 'src/app/enums/documentType.enum';
import { FileTypeEnum } from 'src/app/enums/fileType.enum';
import { SendEmailDialogComponent } from 'src/app/modals/send-email-dialog/send-email-dialog.component';
import { WidgetConfig } from 'src/app/models/dashboard/dashboard-widget.model';
import { WIDGET_CONFIG } from 'src/app/shared/grid/baseGrid.component';
import { openFileInNewTab } from 'src/app/shared/utils/file-link.util';
import { formatFileSizeInMB } from 'src/app/shared/utils/file-size.util';

@Component({
  standalone: false,
    selector: 'app-file-attachment-list',
    templateUrl: './../../shared/grid/core-grid/core-grid.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./../../shared/grid/core-grid/core-grid.component.scss'],
})
export class FileAttachmentListComponent extends CoreGridComponent<FileAttachment> implements OnInit, AfterViewInit {

    protected override get enableCheckboxSelection(): boolean { return true; }

    organizationLevels: Lookup[] = [];
    users: Lookup[] = [];

    constructor(
        protected fileAttachmentService: FileAttachmentService,
        toastService: ToastService,
        commonService: CommonService,
        dialog: MatDialog,
        authService: AuthService,
        gridViewService: GridViewService,
        gridSettingsService: GridSettingsService,
        private organizationLevelService: OrganizationLevelService,
        private userService: UserService,
        private dateFormatService: DateFormatService,
        @Optional() @Inject(WIDGET_CONFIG) widgetConfig?: WidgetConfig
    ) {
        super(
            toastService,
            dialog,
            commonService,
            authService,
            gridViewService,
            gridSettingsService,
            widgetConfig
        );
        this.gridService = fileAttachmentService;
    }

    protected get objectId(): string {
        return RolePermissionObjects.FileAttachment;
    }

    protected override get isGridViewEnabled(): boolean {
        return true;
    }

    ngOnInit(): void {
        super.ngOnInit();

        forkJoin({
            organizationLevels: this.organizationLevelService.getAll(),
            users: this.userService.getAll()
        }).subscribe(results => {
            this.organizationLevels = results.organizationLevels;
            this.users = results.users;
        });

        this.setAdditionalButton();
    }

    ngAfterViewInit(): void {
        super.ngAfterViewInit();
        this.loadHeaderData();
    }

    get hasOrgFilter() {
        return true;
    }

    override initializeProperties(): void {
        this.addEntityTitle = 'File Attachment';
        this.sortActive = nameOf<FileAttachment>(x => x.dateCreated);
        this.defaultOrderBy = 'desc';
        this.searchPlaceholder = 'Search File Attachments';
        this.addDisabled = true;
        this.editDisabled = true;
    }

    override initializeColumns(): void {
        this.columnConfigs = [
            new ColumnConfig<FileAttachment>({
                name: nameOf<FileAttachment>(x => x.name),
                displayName: 'File Name',
                cellTemplate: (file: FileAttachment) => file.name || '-'
            }),
            new ColumnConfig<FileAttachment>({
                name: nameOf<FileAttachment>(x => x.description),
                displayName: 'Description',
                cellTemplate: (file: FileAttachment) => file.description || '-'
            }),
            new ColumnConfig<FileAttachment>({
                name: nameOf<FileAttachment>(x => x.associatedEntity),
                displayName: 'Associated Entity',
                cellTemplate: (file: FileAttachment) => file.associatedEntity || '-',
                isSortable: false,
                includeInGridSettings: false
            }),
            new ColumnConfig<FileAttachment>({
                name: nameOf<FileAttachment>(x => x.dateCreated),
                displayName: 'Date Uploaded',
                cellTemplate: (file: FileAttachment) => file.dateCreated ? this.dateFormatService.convertUTCDateStringToUserTimeZoneString(file.dateCreated.toString()) : '-'
            }),
            new ColumnConfig<FileAttachment>({
                name: nameOf<FileAttachment>(x => x.fileType),
                displayName: 'File Type',
                cellTemplate: (file: FileAttachment) => file.fileType != null ? this.commonService.getEnumName(FileTypeEnum, file.fileType) : '-',
                enumFilterMap: this.enumToValueNameArray(FileTypeEnum)
            }),
            new ColumnConfig<FileAttachment>({
                name: nameOf<FileAttachment>(x => x.createdByUserId),
                displayName: 'Uploaded By',
                cellTemplate: (file: FileAttachment) => {
                    const user = this.users?.find(u => u.id.toString() === file.createdByUserId);
                    return user ? user.name : '-';
                }
            }),
            new ColumnConfig<FileAttachment>({
                name: nameOf<FileAttachment>(x => x.fileSizeInBytes),
                displayName: 'File Size (MB)',
                cellTemplate: (file: FileAttachment) => formatFileSizeInMB(file.fileSizeInBytes)
            }),
            new ColumnConfig<FileAttachment>({
                name: nameOf<FileAttachment>(x => x.organizationLevelId),
                displayName: 'Visibility',
                cellTemplate: (file: FileAttachment) => {
                    const orgLevel = this.organizationLevels.find(o => o.id === file.organizationLevelId);
                    return orgLevel ? orgLevel.name : '-';
                }
            }),
            new ColumnConfig<FileAttachment>({
                name: nameOf<FileAttachment>(x => x.documentType),
                displayName: 'Document Type',
                cellTemplate: (file: FileAttachment) => file.documentType != null ? this.commonService.getEnumName(DocumentType, file.documentType) : '-',
                enumFilterMap: this.enumToValueNameArray(DocumentType)
            }),
            new ColumnConfig<FileAttachment>({
                name: nameOf<FileAttachment>(x => x.dateUpdated),
                displayName: 'Date Updated',
                cellTemplate: (file: FileAttachment) => file.dateUpdated ? this.dateFormatService.convertUTCDateStringToUserTimeZoneString(file.dateUpdated.toString()) : '-'
            }),
            { name: 'action', displayName: 'Action' }
        ];
        this.additionalActions = [
            {
                name: 'Download',
                icon: 'fas fa-arrow-alt-circle-down',
                action: (file: FileAttachment) => this.downloadFile(file)
            }
        ];

    }

    protected override onSelectionChanged(): void {
        this.setAdditionalButton();
    }

    setAdditionalButton() {
        this.additionalButtons = [
            {
                title: 'Send Email',
                readonly: this.selectionService!.selectedItems.length === 0,
                onClick: () => this.openSendEmailDialog()
            }
        ];
    }

    override objectFilterMap(propertyName: string): any | undefined {
        switch (propertyName) {
            case nameOf<FileAttachment>(x => x.organizationLevelId):
                return this.organizationLevelService;
            case nameOf<FileAttachment>(x => x.createdByUserId):
                return this.userService;
            default:
                return undefined;
        }
    }

    override initializeOverviewConfigs(): void {
        this.overviewConfigs = [];
    }

    override initializeTabs(): { routerLink: string; title: string; }[] {
        return [
            { routerLink: '/dashboard/file-attachments', title: 'File Attachments' }
        ];
    }

    override getOverviewData(): Observable<void> {
        return of(undefined);
    }

    override openEntityDialog(entityId?: string): void {
        return;
    }

    override openEntityDeleteDialog(entity: FileAttachment): void {
        return;
    }

    downloadFile(file: FileAttachment): void {
        if (!openFileInNewTab(file.url)) {
            this.toastService.displayErrorMessage('This file cannot be downloaded', true);
        }
    }

    openSendEmailDialog(): void {
        const selectedAttachments = this.selectionService!.selectedItems;
        if (selectedAttachments.length === 0) {
            this.toastService.displayErrorMessage('Please select at least one file attachment', true);
            return;
        }

        const dialogRef = this.dialog.open(SendEmailDialogComponent, {
            width: '750px',
            maxWidth: '90vw',
            data: {
                preSelectedAttachments: selectedAttachments
            }
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result?.success) {
                this.selectionService!.clear();
                this.setAdditionalButton();
            }
        });
    }
}
